// Single point of construction for media-asset public URLs.
//
// All media delivery flows through `media.siflyfoils.com` — the
// `sifly-media-proxy` Worker (infra/workers/sifly-media-proxy, contract in
// docs/reference/media-delivery.md). Every photo is stored ONCE, as the R2
// original; the Worker cuts fixed-size derivatives from it on first request:
//
//   <owner>/<media>/original.<jpg|jpeg|png|webp|gif>/<size>[?v=<rev>]  raster
//   <storage-path>                                                     SVG/PDF/doc, untransformed
//
// The storage path alone decides which (Cloudflare Images retirement,
// Phases 2b–3: nothing reads or writes the second copy). The Worker refuses any other
// derivative shape, any size outside its table and any query but `?v=<n>`,
// so this file emits exactly those.
//
// Inlined-utility note: `apps/portal/api/` serverless functions use the copy
// at `apps/portal/api/_lib/mediaUrl.ts` (mediaUrl + pickSize only). Keep the
// two in sync — apps/portal/tests/unit/mediaUrlInline.test.ts pins it.

const CF_MEDIA_HOST = 'media.siflyfoils.com'

/**
 * The public derivative sizes (policy.ts DERIVATIVE_SIZES minus the
 * portal-only `vision`). `w320/w640/w1920` scale down; `sq` is the 1024
 * square product-card crop (D1); `og` the 1200×630 share crop; `icon` a
 * 180×180 PNG. `sq` and `og` anchor on the dealer's focal point, which the
 * Worker reads from the original — never from the URL.
 */
export const MEDIA_SIZES = ['w320', 'w640', 'w1920', 'sq', 'og', 'icon'] as const
export type MediaSize = (typeof MEDIA_SIZES)[number]

export type MediaUrlOptions = {
  /** Target rendered width in CSS pixels. Snaps up to w320 / w640, else w1920. */
  width?: number
  /** Explicit size; wins over `width`. */
  variant?: MediaSize
}

type MediaUrlInput = {
  original_storage_path?: string | null
  original_format?: string | null
  /**
   * `media_items.image_revision` (migration 167), emitted as `?v=<rev>` on
   * derivatives. Crop / rotate rewrite the original in place and bump it, so
   * the URL changes exactly when the pixels do — which is what lets the
   * Worker send a 1-year `immutable` header. Omitted → an unversioned URL the
   * Worker caches for an hour: a lost optimization, never a stale image.
   */
  image_revision?: number | null
}

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'

/** An original the Worker can transform (policy.ts DERIVATIVE_PATH_RE, minus the size). */
const DERIVATIVE_SOURCE_RE = new RegExp(`^${UUID}/${UUID}/original\\.(?:jpe?g|png|webp|gif)$`)

function pickSize(opts: MediaUrlOptions): MediaSize {
  if (opts.variant) return opts.variant
  const width = opts.width
  if (width && width > 0 && width <= 320) return 'w320'
  if (width && width > 0 && width <= 640) return 'w640'
  // D2: w1024 and w4096 are gone. next/image and srcset downscale w1920.
  return 'w1920'
}

/**
 * Build a public URL for a stored media asset.
 *
 * String input is treated as an R2 path (legacy callers holding only a path,
 * e.g. an AI pipeline that wants the untransformed original).
 */
export function mediaUrl(input: MediaUrlInput | string, opts: MediaUrlOptions = {}): string {
  if (typeof input === 'string') {
    return `https://${CF_MEDIA_HOST}/${input}`
  }
  const path = input.original_storage_path
  if (!path) return ''
  if (DERIVATIVE_SOURCE_RE.test(path)) {
    const v = input.image_revision != null ? `?v=${input.image_revision}` : ''
    return `https://${CF_MEDIA_HOST}/${path}/${pickSize(opts)}${v}`
  }
  return `https://${CF_MEDIA_HOST}/${path}`
}

// ── Rewriters for URLs that arrive as strings ────────────────────────────
//
// Free-form URL columns (`tenants.og_image_url`, `content_pages.og_image_url`,
// `blog_posts.og_image_url`) and client-persisted cart photos hold URLs, not
// rows. These swap the size segment of a derivative URL:
//
//   https://media.siflyfoils.com/<owner>/<media>/original.<ext>/<size>
//
// Anything else passes through unchanged. That includes a pre-Phase-2b
// Cloudflare Images URL (path `m/<id>/<variant>`, e.g. a cart saved in a
// browser before then): the Worker answers it with a 301 to the derivative
// (infra/workers/sifly-media-proxy, legacyMedia.ts), so it still loads.

const DERIVATIVE_URL_RE = new RegExp(
  `^(https://${CF_MEDIA_HOST}/${UUID}/${UUID}/original\\.(?:jpe?g|png|webp|gif))/(?:w320|w640|w1920|sq|og|icon)$`,
)

/**
 * Split a trailing query off a URL: `…/w1920?v=3` → `['…/w1920', '?v=3']`.
 * The portal decorates URLs with `?v=<image_revision>`, and those strings get
 * persisted; matching the bare path and re-appending the query keeps the
 * rewriters working on them instead of silently passing them through.
 */
function splitQuery(url: string): [base: string, query: string] {
  const i = url.indexOf('?')
  return i === -1 ? [url, ''] : [url.slice(0, i), url.slice(i)]
}

function rewrite(url: string, size: MediaSize): string {
  const [base, query] = splitQuery(url)
  const d = base.match(DERIVATIVE_URL_RE)
  return d ? `${d[1]}/${size}${query}` : url
}

/**
 * Stale-Supabase detector. The `media-originals` bucket was deleted at the
 * 2026-05-19 cutover; a free-form column can still carry a pre-migration URL
 * that 404s. Emitting nothing beats emitting a 404 — crawlers fall back to
 * the page's first image.
 */
const STALE_SUPABASE_RE = /^https:\/\/[a-z0-9]+\.supabase\.co\/storage\/v1\/object\/public\/media-originals\//

/**
 * Rewrite a media URL to the 1200×630 `og` share crop. Unchanged for
 * external URLs and R2-served vectors; null for null input or a dead
 * Supabase URL, so `openGraph.images` callers can pass it straight through.
 */
export function asOgVariantIfPossible(url: string | null | undefined): string | null {
  if (!url) return null
  if (STALE_SUPABASE_RE.test(url)) return null
  return rewrite(url, 'og')
}

/**
 * Rewrite a media URL to the small `w320` size, for cart-drawer and checkout
 * thumbnails. Those slots render it `object-contain`, so the whole product
 * stays visible — what the retired white pad-to-square `thumb1x1` variant
 * gave; a square crop would clip long boards and foils.
 */
export function asThumbVariantIfPossible(url: string | null | undefined): string | null {
  if (!url) return null
  return rewrite(url, 'w320')
}

/**
 * Rewrite a media URL to the `sq` product-card crop (catalog cards,
 * carousels, upsell rows, category tiles). The crop's anchor is the dealer's
 * focal point, which the Worker reads from the original's metadata; without
 * one it uses content-aware `auto` gravity, the framing cards had before.
 * Returns null for null input.
 */
export function cropSquareUrl(url: string | null | undefined): string | null {
  if (!url) return null
  return rewrite(url, 'sq')
}

/**
 * Rewrite a derivative URL to the R2 original it was cut from (the version
 * token kept, so the URL still changes when a crop rewrites the bytes).
 *
 * For consumers that cannot take the Worker's AVIF/WebP derivatives: Google
 * Merchant Center accepts JPEG, PNG, WebP, GIF, BMP and TIFF — not AVIF —
 * and the Worker does not negotiate formats per client. The original is
 * whatever media-finalize wrote (JPEG/PNG/WebP/GIF, ≤ 4096 px), which is
 * also the high resolution Shopping asks for. Anything else passes through.
 */
export function asOriginalIfPossible(url: string | null | undefined): string | null {
  if (!url) return null
  const [base, query] = splitQuery(url)
  const d = base.match(DERIVATIVE_URL_RE)
  return d ? `${d[1]}${query}` : url
}
